import { type FC, type ReactNode, useMemo } from 'react';
import styled from 'threads5/styles/styled';
import type { ChangelogProductId } from 'src/components/docs/Changelog/products';
import { LEGACY_PRODUCT_ID } from 'src/components/docs/Changelog/products';
import { useChangelogFilter } from 'src/components/docs/Changelog/ChangelogFilterContext';

const StyledSection = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'isVisible';
  },
})<{ isVisible: boolean }>(({ isVisible }) => {
  return {
    display: isVisible ? 'block' : 'none',
  };
});

/**
 * Wraps a single "For X:" sub-section inside a ChangelogEntry. Hides itself
 * when the active product filter doesn't intersect this section's products.
 * Sections tagged `legacy` (for retired products like Beacon) are hidden
 * whenever ANY filter is active — they only appear in the unfiltered view.
 *
 * The filter is respected even when the parent entry is the URL hash target:
 * a deep link to an entry surfaces the entry's date heading (so the link
 * resolves) but its sub-sections still honor the active filter so the user
 * doesn't see off-filter content they explicitly excluded.
 */
const ChangelogSection: FC<{
  products: ReadonlyArray<ChangelogProductId>;
  children: ReactNode;
}> = ({ products, children }) => {
  const { activeProducts } = useChangelogFilter();

  const isVisible = useMemo<boolean>(() => {
    if (activeProducts.size === 0) return true;
    if (products.includes(LEGACY_PRODUCT_ID)) return false;
    for (const id of products) {
      if (activeProducts.has(id)) return true;
    }
    return false;
  }, [activeProducts, products]);

  return (
    <StyledSection isVisible={isVisible} data-products={products.join(',')}>
      {children}
    </StyledSection>
  );
};

export default ChangelogSection;
